import React, { ReactElement, useState } from "react";

import Radio from "@mui/material/Radio";
import Card, { CardProps } from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { CardActionArea } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Icon } from "semantic-ui-react";

import CardOptionItem from "./CardOptionItem";

type CardRadioButtonProps = {
  label: string;
  image?: string;
  subtitle?: string | ReactElement;
  checked: boolean;
  sx?: CardProps["sx"];
  items?: Record<string, any>[];
  radioPosition?: "left" | "right";
  onClick: () => void;
  onSelect?: (id: number) => void;
};

const useStyles = makeStyles((theme) => ({
  card: {
    "&.MuiPaper-root": {
      borderRadius: "12px",
      marginBottom: "1.5rem",
      boxShadow: "0 1px 6px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02)",
      position: "relative",
      zIndex: 2,
      "& .MuiTouchRipple-ripple": {
        color: "var(--primary-theme-color)",
      },
    },
  },
  checked: {
    border: "2px solid var(--primary-theme-color)",
  },
  unchecked: {
    border: "1px solid #eee",
  },
  cardContent: {
    "&.MuiCardContent-root": {
      padding: "12.5px 25px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  cardContentLeft: {
    "&.MuiCardContent-root": {
      padding: "12.5px 25px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "16px",
    },
  },
  leftSide: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  image: {
    width: "30px",
  },
  label: {
    fontSize: "18px",
    fontWeight: "500",
    color: "#333",
  },
  itemsContainer: {
    border: "1px solid #eee",
    marginBottom: "1.55rem",
    borderRadius: "10px",
    marginTop: "-2.5rem",
    position: "relative",
    zIndex: 1,
    padding: "1rem 0.5rem 0",
    overflow: "hidden",
  },
  optionItem: {
    "&.MuiPaper-root": {
      marginBottom: "0 !important",
      borderRadius: 0,
      "& .MuiButtonBase-root div.MuiCardContent-root": {
        padding: "9px 25px !important",
      },
    },
  },
}));

const CardRadioButton = (props: CardRadioButtonProps) => {
  const classes = useStyles();
  const radioPosition = props.radioPosition || "right";

  const [isToggle, setIsToggle] = useState<boolean>(false);

  const handleClick = () => {
    if (props.items) {
      setIsToggle(!isToggle);

      return;
    }

    props.onClick?.();
  };

  return (
    <>
      <Card
        className={`${classes.card} ${props.checked ? classes.checked : classes.unchecked} ${
          props.checked ? "checked" : ""
        }`}
        sx={props.sx}
      >
        <CardActionArea onClick={handleClick}>
          <CardContent
            className={radioPosition === "left" ? classes.cardContentLeft : classes.cardContent}
          >
            {radioPosition === "left" && (
              <Radio className="radio-check-button" checked={props.checked} disableRipple />
            )}

            {/* Content */}
            <div className={classes.leftSide}>
              {props.image && <img src={props.image} alt="icon" className={classes.image} />}
              <div>
                <span className={`${classes.label} radio-check-label`}>{props.label}</span>
                <div>{props.subtitle}</div>
              </div>
            </div>

            {radioPosition === "right" && (
              <Radio className="radio-check-button" checked={props.checked} disableRipple />
            )}
            {isToggle && (
              <Icon
                name="chevron down"
                style={{
                  color: "rgba(179, 179, 179, 1)",
                  fontSize: "1.5rem",
                  margin: "0 -0.75rem 0 -3rem",
                }}
              />
            )}
          </CardContent>
        </CardActionArea>
      </Card>
      {isToggle && (
        <div className={classes.itemsContainer}>
          {(props.items ?? []).map((item, index, self) => (
            <CardOptionItem
              key={item.value}
              label={item.value}
              className={classes.optionItem}
              style={{
                borderBottom: self.length - 1 === index ? "" : "1px solid #eee",
              }}
              onClick={() => {
                props.onSelect?.(item.id);
              }}
              rightIcon={false}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default CardRadioButton;
